import type { ImageNode } from '@repo/api-contracts';
import { FlipHorizontal2, FlipVertical2 } from 'lucide-react';
import type { ReactElement } from 'react';
import { cn, Input, Label } from '@/shared/ui';
import { RotationField } from './rotation-field';

interface ImagePropertiesPanelProps {
  readonly node: ImageNode;
  readonly onChange: (patch: Partial<ImageNode>) => void;
  readonly onDelete: () => void;
}

const TOGGLE =
  'inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-full border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** 가운데를 지키며 비율대로 가로 폭을 바꾼다. */
const resizeToWidth = (node: ImageNode, width: number): Partial<ImageNode> => {
  const height = (node.height / node.width) * width;
  return {
    width,
    height,
    x: node.x + (node.width - width) / 2,
    y: node.y + (node.height - height) / 2,
  };
};

/** 선택한 이미지 노드 — 크기(가로 폭, 비율 유지)·회전·가로/세로 반전·삭제. 캔버스 핸들로도 바꿀 수 있다. */
export const ImagePropertiesPanel = ({
  node,
  onChange,
  onDelete,
}: ImagePropertiesPanelProps): ReactElement => (
  <div className="flex flex-col gap-4">
    <p className="text-xs leading-relaxed text-muted-foreground">
      상품 위 이미지를 끌어 옮기고, 모서리로 크기(Shift 비율 유지)를, 가운데 핸들로 가로·세로를, 위
      회전 손잡이로 각도를 바꿀 수 있어요.
    </p>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label htmlFor="image-width" className="text-xs">
          가로(mm)
        </Label>
        <Input
          id="image-width"
          type="number"
          className="mt-1.5 px-3"
          min={1}
          step={1}
          value={Math.round(node.width)}
          aria-describedby="image-size"
          onChange={(event) => {
            const width = Number(event.target.value);
            if (Number.isFinite(width) && width >= 1) onChange(resizeToWidth(node, width));
          }}
        />
        <p id="image-size" className="mt-1 text-xs text-muted-foreground tabular-nums">
          {Math.round(node.width)}×{Math.round(node.height)}mm
        </p>
      </div>
      <RotationField
        value={node.rotationDeg}
        onChange={(rotationDeg) => onChange({ rotationDeg })}
      />
    </div>
    <div className="flex gap-2">
      <button
        type="button"
        aria-pressed={node.flipX}
        className={cn(
          TOGGLE,
          node.flipX
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-foreground/15 hover:border-foreground/40',
        )}
        onClick={() => onChange({ flipX: !node.flipX })}
      >
        <FlipHorizontal2 aria-hidden className="size-4" strokeWidth={1.5} />
        가로 뒤집기
      </button>
      <button
        type="button"
        aria-pressed={node.flipY}
        className={cn(
          TOGGLE,
          node.flipY
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-foreground/15 hover:border-foreground/40',
        )}
        onClick={() => onChange({ flipY: !node.flipY })}
      >
        <FlipVertical2 aria-hidden className="size-4" strokeWidth={1.5} />
        세로 뒤집기
      </button>
    </div>
    <button
      type="button"
      className="self-start text-xs text-muted-foreground underline-offset-4 hover:text-destructive hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onDelete}
    >
      이미지 삭제
    </button>
  </div>
);
